import { useState } from 'react';

export default function ServiceTabsSection() {
  const [activeTab, setActiveTab] = useState<'startup' | 'management'>('startup');

  const services = {
    startup: {
      title: '開業サポート',
      subtitle: 'これから民泊を始めたい方へ',
      description: '許認可取得から物件準備まで、開業に必要なすべてをサポートします。',
      features: [
        {
          icon: 'ri-file-list-3-line',
          title: '許認可申請代行',
          description: '旅館業法・消防法など、複雑な許認可手続きを代行します。',
        },
        {
          icon: 'ri-home-gear-line',
          title: '物件準備サポート',
          description: '内装工事・備品調達・写真撮影まで、開業準備を完全サポート。',
        },
        {
          icon: 'ri-book-open-line',
          title: '運営ノウハウ提供',
          description: '価格設定・集客方法など、成功するための運営ノウハウを伝授。',
        },
        {
          icon: 'ri-shield-check-line',
          title: '開業後フォロー',
          description: '開業後も継続的にサポート。運営代行への移行もスムーズです。',
        },
      ],
      image: 'https://public.readdy.ai/ai/img_res/f43b6e0f5881382eef7dc393df6bb0c4.jpg',
    },
    management: {
      title: '運営代行',
      subtitle: 'すでに物件をお持ちの方へ',
      description: '予約管理からゲスト対応まで、日々の運営をすべて代行します。',
      features: [
        {
          icon: 'ri-calendar-check-line',
          title: '予約管理・カレンダー同期',
          description: '複数サイトの予約を一元管理。ダブルブッキングを防ぎます。',
        },
        {
          icon: 'ri-customer-service-2-line',
          title: 'ゲスト対応代行',
          description: '多言語対応のゲスト対応。チェックイン・アウトもお任せください。',
        },
        {
          icon: 'ri-home-smile-line',
          title: '清掃・メンテナンス手配',
          description: '清掃業者の手配から品質管理まで、すべて代行します。',
        },
        {
          icon: 'ri-line-chart-line',
          title: '収益最適化',
          description: '価格調整・稼働率向上など、収益を最大化する施策を実施。',
        },
      ],
      image: 'https://readdy.ai/api/search-image?query=vintage%201970s%20japanese%20traditional%20inn%20management%20service%20professional%20staff%20warm%20sepia%20amber%20tones%20nostalgic%20retro%20showa%20era%20hospitality%20friendly%20atmosphere%20classic%20japanese%20interior%20wooden%20architecture%20heritage%20design%20customer%20service%20excellence&width=800&height=600&seq=management-service-img-001&orientation=landscape',
    },
  };

  const currentService = services[activeTab];

  return (
    <section id="services" className="relative py-20 bg-gradient-to-b from-stone-50 via-amber-50 to-stone-50 font-serif">
      {/* 上部装飾ライン - 市松模様 */}
      <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-transparent via-[#8b2500] to-transparent opacity-60"></div>
      <div className="absolute top-2 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#c9a84c] to-transparent opacity-50"></div>

      {/* 下部装飾ライン - 市松模様 */}
      <div className="absolute bottom-2 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#c9a84c] to-transparent opacity-50"></div>
      <div className="absolute bottom-0 left-0 right-0 h-2 bg-gradient-to-r from-transparent via-[#8b2500] to-transparent opacity-60"></div>

      <div className="max-w-7xl mx-auto px-6">
        {/* セクションタイトル */}
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-stone-800 mb-4">
            ◆ サービス内容 ◆
          </h2>
          
          {/* 金色装飾ライン */}
          <div className="flex items-center justify-center gap-3 mt-6">
            <div className="h-px w-16 bg-gradient-to-r from-transparent to-[#c9a84c]"></div>
            <span className="text-[#8b2500] text-xl">◇</span>
            <div className="h-px w-32 bg-[#c9a84c]"></div>
            <span className="text-[#8b2500] text-xl">◇</span>
            <div className="h-px w-16 bg-gradient-to-l from-transparent to-[#c9a84c]"></div>
          </div>

          <p className="text-stone-600 mt-6 max-w-2xl mx-auto leading-relaxed">
            お客様のニーズに合わせて、2つのサービスをご用意しています
          </p>
        </div>

        {/* タブ切り替え */}
        <div className="flex justify-center mb-12">
          <div className="inline-flex bg-white border-2 border-[#8b2500] rounded-full p-1.5 shadow-lg">
            <button
              onClick={() => setActiveTab('startup')}
              className={`px-8 py-3 rounded-full font-bold text-sm transition-all whitespace-nowrap ${
                activeTab === 'startup'
                  ? 'bg-gradient-to-r from-[#8b2500] to-[#a52a00] text-white shadow-md'
                  : 'text-stone-600 hover:text-stone-900'
              }`}
            >
              <i className="ri-rocket-line mr-2"></i>
              開業サポート
            </button>
            <button
              onClick={() => setActiveTab('management')}
              className={`px-8 py-3 rounded-full font-bold text-sm transition-all whitespace-nowrap ${
                activeTab === 'management'
                  ? 'bg-gradient-to-r from-[#8b2500] to-[#a52a00] text-white shadow-md'
                  : 'text-stone-600 hover:text-stone-900'
              }`}
            >
              <i className="ri-settings-3-line mr-2"></i>
              運営代行
            </button>
          </div>
        </div>

        {/* サービス詳細 */}
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* 左側：画像 */}
          <div className="relative">
            {/* 朱色・金色の二重枠線 */}
            <div className="absolute -inset-3 border-4 border-[#8b2500] rounded-lg"></div>
            <div className="absolute -inset-1 border-2 border-[#c9a84c] rounded-lg"></div>
            
            {/* 四隅装飾 */}
            <div className="absolute -top-4 -left-4 w-10 h-10 border-t-4 border-l-4 border-[#8b2500]"></div>
            <div className="absolute -top-4 -right-4 w-10 h-10 border-t-4 border-r-4 border-[#8b2500]"></div>
            <div className="absolute -bottom-4 -left-4 w-10 h-10 border-b-4 border-l-4 border-[#8b2500]"></div>
            <div className="absolute -bottom-4 -right-4 w-10 h-10 border-b-4 border-r-4 border-[#8b2500]"></div>

            <div className="relative rounded-lg overflow-hidden">
              <img
                src={currentService.image}
                alt={currentService.title}
                className="w-full h-[500px] object-cover sepia-[0.3] contrast-110"
              />
            </div>
          </div>

          {/* 右側：コンテンツ */}
          <div>
            <div className="inline-block bg-[#8b2500] text-[#c9a84c] text-xs font-bold px-4 py-1.5 rounded-full mb-4">
              {currentService.subtitle}
            </div>
            
            <h3 className="text-3xl md:text-4xl font-bold text-stone-800 mb-4">
              ◇ {currentService.title}
            </h3>
            
            <p className="text-stone-600 mb-8 leading-relaxed text-lg">
              {currentService.description}
            </p>

            <div className="space-y-6">
              {currentService.features.map((feature, index) => (
                <div
                  key={index}
                  className="group relative bg-gradient-to-b from-stone-50 to-amber-50/50 p-6 rounded-lg border-2 border-[#8b2500]/20 hover:border-[#8b2500]/40 transition-all duration-300 hover:shadow-lg"
                >
                  {/* 左端アクセントライン */}
                  <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#8b2500] rounded-l-lg"></div>

                  <div className="flex items-start gap-4">
                    {/* アイコン */}
                    <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-b from-[#8b2500] to-[#6b1d00] rounded-lg flex-shrink-0 group-hover:scale-110 transition-transform duration-300 shadow-md">
                      <i className={`${feature.icon} text-2xl text-[#c9a84c]`}></i>
                    </div>

                    <div className="flex-1">
                      <h4 className="text-lg font-bold text-stone-800 mb-2">
                        {feature.title}
                      </h4>
                      <p className="text-stone-600 text-sm leading-relaxed">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8">
              <a
                href="#contact"
                className="inline-flex items-center gap-2 bg-gradient-to-r from-[#8b2500] to-[#a52a00] text-white font-bold px-8 py-4 rounded-lg hover:shadow-xl hover:-translate-y-0.5 transition-all whitespace-nowrap"
              >
                <span className="text-[#c9a84c]">✦</span>
                詳しく相談する
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}